
import { isValid, isBefore, parse } from 'date-fns';
import { CycleImportRow, ValidationResult } from './types';
import { ValidationError } from '../types';

/**
 * Parses and validates a date value from various formats
 */
export function parseDateValue(value: any): Date | null {
  // Handle Excel date (number)
  if (typeof value === 'number') {
    // Excel dates are number of days since December 30, 1899
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return isValid(date) ? date : null;
  }
  
  // Handle string date
  if (typeof value === 'string') {
    // Try parsing as YYYY-MM-DD
    const dateFormat = 'yyyy-MM-dd';
    const parsedDate = parse(value, dateFormat, new Date());
    
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    
    // Try alternate formats
    const alternateFormats = ['MM/dd/yyyy', 'dd/MM/yyyy', 'MM-dd-yyyy', 'dd-MM-yyyy'];
    for (const format of alternateFormats) {
      const parsed = parse(value, format, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }
  }
  
  // Handle JavaScript Date
  if (value instanceof Date && isValid(value)) {
    return value;
  }
  
  return null;
}

/**
 * Validates the header row of an imported file
 */
export function validateHeaderRow(headerRow: string[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const expectedHeaders = [
    'Cycle Start Date', 
    'Cycle End Date', 
    'Symptoms Logged', 
    'Mood', 
    'Medications Taken', 
    'Contraceptive Use', 
    'Notes'
  ];
  
  for (let i = 0; i < expectedHeaders.length; i++) {
    if (!headerRow[i] || !headerRow[i].includes(expectedHeaders[i])) {
      errors.push({
        rowIndex: 0,
        message: `Column "${expectedHeaders[i]}" is missing or incorrectly named.`,
        field: 'header',
        type: 'error'
      });
    }
  }
  
  return errors;
}

/**
 * Validates a data row and converts it to a CycleImportRow object
 */
export function validateDataRow(row: any[], rowIndex: number): {
  rowData: Partial<CycleImportRow>;
  errors: ValidationError[];
  warnings: ValidationError[];
} {
  const rowData: Partial<CycleImportRow> = { rowIndex };
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  // Parse start date
  if (row[0]) {
    const startDate = parseDateValue(row[0]);
    if (startDate) {
      rowData.cycleStartDate = startDate;
    } else {
      errors.push({
        rowIndex,
        message: `Invalid start date format. Use YYYY-MM-DD format.`,
        field: 'cycleStartDate',
        type: 'error'
      });
    }
  } else {
    errors.push({
      rowIndex,
      message: 'Cycle Start Date is required.',
      field: 'cycleStartDate',
      type: 'error'
    });
  }
  
  // Parse end date
  if (row[1]) {
    const endDate = parseDateValue(row[1]);
    if (endDate) {
      rowData.cycleEndDate = endDate;
      
      // Check if end date is after start date
      if (rowData.cycleStartDate && isBefore(endDate, rowData.cycleStartDate)) {
        errors.push({
          rowIndex,
          message: 'End date cannot be earlier than start date.',
          field: 'cycleEndDate',
          type: 'error'
        });
      }
    } else {
      errors.push({
        rowIndex,
        message: `Invalid end date format. Use YYYY-MM-DD format.`,
        field: 'cycleEndDate',
        type: 'error'
      });
    }
  } else {
    errors.push({
      rowIndex,
      message: 'Cycle End Date is required.',
      field: 'cycleEndDate',
      type: 'error'
    });
  }
  
  // Parse symptoms
  if (row[2]) {
    const symptomsText = String(row[2]).trim();
    rowData.symptoms = symptomsText.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  // Parse mood
  if (row[3]) {
    const moodValue = String(row[3]).trim();
    if (['Low', 'Medium', 'High'].includes(moodValue)) {
      rowData.mood = moodValue as 'Low' | 'Medium' | 'High';
    } else {
      warnings.push({
        rowIndex,
        message: 'Mood must be one of: Low, Medium, High.',
        field: 'mood',
        type: 'warning'
      });
    }
  }
  
  // Parse medications
  if (row[4]) {
    const medicationsText = String(row[4]).trim();
    rowData.medications = medicationsText.split(',').map(m => m.trim()).filter(Boolean);
  }
  
  // Parse contraceptive use
  if (row[5]) {
    rowData.contraceptiveUse = String(row[5]).trim();
    
    // Validate contraceptive options
    const validOptions = ['Birth Control Pill', 'IUD', 'Implant', 'Patch', 'Ring', 'None'];
    if (!validOptions.includes(rowData.contraceptiveUse)) {
      warnings.push({
        rowIndex,
        message: 'Invalid contraceptive option.',
        field: 'contraceptiveUse',
        type: 'warning'
      });
    }
  }
  
  // Parse notes
  if (row[6]) {
    rowData.notes = String(row[6]).trim();
  }
  
  return { rowData, errors, warnings };
}
